// Deciding a race candidate by running — step 2 of docs/design/race-suggestions.md.
//
// The detector (race.ts) proposes; this decides. Its whole reason to exist is
// that the pattern alone cannot tell `withdraw`'s genuine lost update from the
// `Future`/`Chan` library code that reads a shared cell across two transactions
// *on purpose* — both are the same A-B-A shape. What separates them is not how
// they look but what happens when you run them: does the pair's ordering
// actually change where the program ends up?
//
// **The test.** The candidate came from a run where the intruder fell *between*
// the victim's two accesses (the A-B-A). Serialize that pair — make the two
// accesses adjacent, the intrusion entirely before or entirely after — and run
// it. If a serialization is reachable and lands the program somewhere else, the
// non-atomicity was observable: keep it. If none is reachable, or every
// reachable one agrees with the observed ending, there is nothing to report.
//
//   lost_update:  observed `balance 90` (interleaved) vs serialized `balance 80`
//                 — different, so real.
//   main's pair:  `sleep(1.0)` forces the withdrawals before the print, so no
//                 schedule moves the read off the far side of the write. No
//                 serialization is reachable — not racy, merely non-atomic.
//   the library:  completing a future before vs after a read gives the same
//                 ending, so the reads not being atomic changes nothing.
//
// **Serializing is a programmatic drag.** The playground already turns "run this
// step before that one" into a schedule override (reorder.ts). Pushing the
// victim's first access down onto the intruder's step drags the whole intrusion
// ahead of the pair; pulling the victim's last access up onto it drags the
// intrusion behind. Same machinery the user's pointer drives, aimed by the
// candidate instead. A serialization that merely reorders a step or two without
// changing the ending is inconclusive, not a disproof — so a candidate is
// confirmed when *some* reachable serialization differs, and only dropped when
// none does. Step 3 then verifies the actual `txn` fix, which is the real gate.

import { run } from "../src/compiler.js";
import type { RunResult } from "../src/compiler.js";
import type { ScheduleOverride } from "../src/backend/interp/interpreter.js";
import { canDrop, stepOfRow, toOverride, toReorder } from "./reorder.js";
import type { Reorder } from "./reorder.js";
import { endingOf } from "./timeline.js";
import type { Ending } from "./timeline.js";
import { raceCandidates } from "./race.js";
import type { RaceCandidate } from "./race.js";

/** The run parameters that pick a program's execution, minus the schedule —
 *  everything `decideRace` must hold fixed while it varies only the ordering. */
export interface RunParams {
  seed?: number;
  maxTime?: number;
  strategy?: "occ" | "hybrid";
}

export type OracleVerdict =
  /** A reachable serialization lands the program on a different ending. Carries
   *  both the raw override (to reproduce the run) and the same drag as a stored
   *  `Reorder` (for the playground to replay — the serialized *history* the user
   *  can open, the mirror of the observed one). */
  | { kind: "confirmed"; observed: Ending; serialized: Ending; schedule: ScheduleOverride; serialization: Reorder }
  /** The pair could not be serialized by any legal reordering — the intrusion is
   *  forced, so the pair is non-atomic but not racy. */
  | { kind: "unreachable" }
  /** Every reachable serialization ends where the observed run did, so the
   *  ordering does not change the outcome (or does not change one we can see). */
  | { kind: "indifferent"; observed: Ending };

/** A canonical string for an ending, so two are compared by what they contain
 *  rather than by identity: every assertion's verdict, everything printed, and
 *  the final value at each address. Folding assertions in is what lets a run that
 *  fails `assert a.balance == 80` count as a *different* outcome from one that
 *  passes it — the divergence the detector is looking for. */
export function endingKey(e: Ending): string {
  const final = [...e.final].sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  return JSON.stringify({ asserts: e.asserts, prints: e.prints, final });
}

/**
 * The schedules that serialize a candidate's pair, as programmatic drags.
 *
 * Two directions, because either makes the pair adjacent:
 *  - push the victim's *first* access down onto the intruder's step — the drag
 *    window swallows everything up to it, so the whole intrusion lands ahead of
 *    the pair;
 *  - pull the victim's *last* access up onto the intruder's step — the intrusion
 *    lands behind the pair.
 *
 * Only legal drags are returned (same virtual instant, never crossing the step
 * that enabled the moved one), so an intrusion pinned in place by `sleep` or by
 * causation yields nothing — which is the point for `main`'s forced pair.
 */
/** One way to serialize the pair: the raw override that runs it, and the same
 *  drag stored by id so the playground can replay it. */
export interface Serialization {
  schedule: ScheduleOverride;
  reorder: Reorder;
}

export function serializations(base: RunResult, cand: RaceCandidate): Serialization[] {
  const c = base.causality;
  if (!c) return [];
  const first = stepOfRow(c, cand.first.row);
  const mid = stepOfRow(c, cand.middle.row);
  const last = stepOfRow(c, cand.last.row);
  const out: Serialization[] = [];
  for (const moved of [first, last]) {
    if (moved < 0 || mid < 0 || moved === mid) continue;
    if (canDrop(c, moved, mid)) out.push({ schedule: toOverride(c, moved, mid), reorder: toReorder(c, moved, mid) });
  }
  return out;
}

/**
 * Decide one candidate against the run it came from.
 *
 * `base` must be the causality-bearing run whose trace produced `cand` (its rows
 * index into `base.trace`), and `params` must be that run's own parameters, so
 * the serializations differ from it in ordering alone.
 */
export function decideRace(
  source: string,
  params: RunParams,
  base: RunResult,
  cand: RaceCandidate
): OracleVerdict {
  const observed = endingOf(base);
  const observedKey = endingKey(observed);
  let anyReachable = false;
  for (const { schedule, reorder } of serializations(base, cand)) {
    let r: RunResult;
    try {
      r = run(source, { ...params, causality: true, schedule });
    } catch {
      continue; // this ordering deadlocks or runs past budget — not reachable
    }
    if (r.scheduleAbandoned) continue; // the override asked for the impossible
    anyReachable = true;
    const serialized = endingOf(r);
    if (endingKey(serialized) !== observedKey) {
      return { kind: "confirmed", observed, serialized, schedule, serialization: reorder };
    }
  }
  return anyReachable ? { kind: "indifferent", observed } : { kind: "unreachable" };
}

/** A confirmed race, with the evidence that confirmed it. */
export interface ConfirmedRace {
  candidate: RaceCandidate;
  observed: Ending;
  serialized: Ending;
  /** The override that realized the different ending. */
  schedule: ScheduleOverride;
  /** The same reordering stored by id, so the playground can open the serialized
   *  history as a first-class timeline beside the observed one. */
  serialization: Reorder;
}

/**
 * The races the oracle confirms in a run *already in hand* — detect the
 * candidates of `base.trace`, decide each against `base`.
 *
 * Takes the run rather than producing it, so a caller with a specific execution
 * on screen (the happens-before graph, say) can ask "which races are in *this*
 * one" and get answers whose event rows index into that very trace.
 */
export function confirmedRacesIn(source: string, params: RunParams, base: RunResult): ConfirmedRace[] {
  if (!base.causality) return [];
  const out: ConfirmedRace[] = [];
  for (const candidate of raceCandidates(base)) {
    const verdict = decideRace(source, params, base, candidate);
    if (verdict.kind === "confirmed") {
      out.push({
        candidate,
        observed: verdict.observed,
        serialized: verdict.serialized,
        schedule: verdict.schedule,
        serialization: verdict.serialization,
      });
    }
  }
  return out;
}

/**
 * Run the program once and return only the races the oracle confirms.
 *
 * Single-seed: it decides the candidates of *this* run against *this* run. The
 * detector is dynamic, so a fuller search samples several seeds and unions the
 * results — that belongs to the layer that drives the panel (step 3), not here,
 * where the contract is simply "confirmed against one execution".
 */
export function confirmedRaces(source: string, params: RunParams): ConfirmedRace[] {
  return confirmedRacesIn(source, params, run(source, { ...params, causality: true }));
}
